import * as React from "react";
import { styled } from '@mui/material/styles';
import Box from "@mui/material/Box";
import Button from '@mui/material/Button';
import { processCalib } from '../../util/trialApi';
import { Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, RadioGroup, FormControlLabel, Radio, FormLabel, Alert } from '@mui/material';
import Fade from '@mui/material/Fade';
import '../../../dist/assets/stylesheets/css/recordPatient.css'
import { Icon } from '@iconify/react';
import { deleteCalibration, trashCalibrations } from "../../util/recordingAPI";
import Tooltip from '@mui/material/Tooltip';
import ConfirmModal from '../modals/ConfirmModal';
import { useDispatch } from 'react-redux';
import { notificationActions } from '../../store/notification-slice';

const CommonButton = styled(Button)({
    color: 'black',
    textTransform: 'none',
    fontFamily: 'Inter, sans-serif',
    fontSize: '1rem',
    fontWeight: 400,
    '& svg': {
        fontSize: '1.1rem',
        marginRight: '5px',
    }
});

const CircleButton = styled(Button)({
    color: 'black',
    textTransform: 'none',
    fontFamily: 'Inter, sans-serif',
    borderRadius: '50%',
    padding: '5px',
    minWidth: '40px',
    height: '40px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    outline: 'none',
    fontSize: '1rem',
});

const getCalibrationStageLabel = (progress) => {
    if (progress >= 95 && progress < 100) return 'Finalizing';
    if (progress >= 90) return 'Diagnostics';

    // 5–85%: dominant board detection phase across cameras
    if (progress >= 5 && progress < 25) return 'Cam 1 detection';
    if (progress >= 25 && progress < 45) return 'Cam 2 detection';
    if (progress >= 45 && progress < 65) return 'Cam 3 detection';
    if (progress >= 65 && progress < 85) return 'Cam 4 detection';

    if (progress >= 3) return 'Camera init & sync';
    if (progress >= 1) return 'Starting';
    return 'Queued';
};

const getStatusInfo = (status, err_msg) => {
    let color;
    let tooltipText = '';
    switch (status) {
        case 'success':
            color = "#9FD06A";
            break;
        case 'ready':
            color = "#E7D859";
            break;
        case 'failed':
            color = "#DD6364";
            tooltipText = err_msg || 'Processing failed.';
            break;
        case 'weak':
            color = "#FFA500";
            break;
        case 'uploading':
            color = "#92CEFA";
            break;
        case 'processing':
            color = "#E7D859";
            break;
        default:
            color = "#BDBDBD";
    }
    return { color, tooltipText };
};

const CalibrationTable = ({ data, setCalibrations, selectCalibrationView }) => {
    const dispatch = useDispatch();
    const [openDialog, setOpenDialog] = React.useState(false);
    const [currentId, setCurrentId] = React.useState(null);
    const [isDeleting, setIsDeleting] = React.useState(false);
    const [openBoardTypeDialog, setOpenBoardTypeDialog] = React.useState(false);
    const [boardType, setBoardType] = React.useState('5x7');
    const [selectedCalibId, setSelectedCalibId] = React.useState(null);
    const [page, setPage] = React.useState(0);
    const [rowsPerPage, setRowsPerPage] = React.useState(10);
    const [selectedCalibIds, setSelectedCalibIds] = React.useState(new Set());
    const [trashConfirmOpen, setTrashConfirmOpen] = React.useState(false);
    const [isTrashing, setIsTrashing] = React.useState(false);

    const handleProcessCalib = async () => {
        const calibID = selectedCalibId;
        const index = data.findIndex(calib => calib.id === calibID);

        // Optimistically update UI immediately
        if (index !== -1) {
            const next = [...data];
            next[index] = {
                ...next[index],
                status: 'processing',
                // Clear any stale progress; polling will fill in real values
                progress: null,
            };
            setCalibrations(next);
        }

        // Close dialog right away so the click feels responsive
        setOpenBoardTypeDialog(false);

        try {
            await processCalib(calibID, boardType);
        } catch (error) {
            console.error("Failed to process calibration", error);
        }
    };

    const handleOpenBoardTypeDialog = (event, calibID) => {
        event.stopPropagation();
        setSelectedCalibId(calibID);
        setBoardType('5x7'); // Reset to default
        setOpenBoardTypeDialog(true);
    };

    const handleCloseBoardTypeDialog = () => {
        setOpenBoardTypeDialog(false);
        setSelectedCalibId(null);
    };

    const handleClickOpen = (id) => {
        setCurrentId(id);
        setOpenDialog(true);
    };

    const handleClose = () => {
        setOpenDialog(false);
        setIsDeleting(false);
    };

    const rows = data || [];
    const totalRows = rows.length;
    const paginatedRows = rows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

    const toggleCalibSelection = (id) => {
        setSelectedCalibIds((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };
    const selectAllOnPage = () => {
        const pageIds = paginatedRows.map((r) => r.id);
        const allSelected = pageIds.length > 0 && pageIds.every((id) => selectedCalibIds.has(id));
        if (allSelected) {
            setSelectedCalibIds((prev) => {
                const next = new Set(prev);
                pageIds.forEach((id) => next.delete(id));
                return next;
            });
        } else {
            setSelectedCalibIds((prev) => {
                const next = new Set(prev);
                pageIds.forEach((id) => next.add(id));
                return next;
            });
        }
    };
    const selectAllChecked = paginatedRows.length > 0 && paginatedRows.every((r) => selectedCalibIds.has(r.id));
    const selectAllIndeterminate = paginatedRows.some((r) => selectedCalibIds.has(r.id)) && !selectAllChecked;

    const handleTrashConfirm = () => {
        const ids = Array.from(selectedCalibIds);
        if (ids.length === 0) return;
        setIsTrashing(true);
        trashCalibrations(ids)
            .then(() => {
                setCalibrations((prev) => prev.filter((c) => !ids.includes(c.id)));
                setSelectedCalibIds(new Set());
                setTrashConfirmOpen(false);
                dispatch(notificationActions.setNotification({ show: true, status: 'success', message: 'Moved to trash.' }));
            })
            .catch((err) => {
                dispatch(
                    notificationActions.setNotification({
                        show: true,
                        status: 'error',
                        message: err?.response?.data?.reason || err?.message || 'Failed to move to trash.',
                    })
                );
            })
            .finally(() => setIsTrashing(false));
    };

    return (
        <Box mt={2} mb={2}>
            <section className="patient-table container recordings">
                <div className="selection-toolbar-slot">
                    {selectedCalibIds.size > 0 && (
                        <div className="selection-toolbar">
                            <span className="selection-toolbar__count">{selectedCalibIds.size} calibration(s) selected</span>
                            <button type="button" className="svg-btn selection-toolbar__btn" onClick={() => setTrashConfirmOpen(true)}>
                                Move to trash
                            </button>
                            <button type="button" className="svg-btn selection-toolbar__btn selection-toolbar__btn--secondary" onClick={() => setSelectedCalibIds(new Set())}>
                                Clear selection
                            </button>
                        </div>
                    )}
                </div>
                <table className="calibration-table">
                    <thead>
                        <tr>
                            <th className="table-header-checkbox">
                                <input
                                    type="checkbox"
                                    checked={selectAllChecked}
                                    ref={(el) => el && (el.indeterminate = selectAllIndeterminate)}
                                    onChange={selectAllOnPage}
                                    aria-label="Select all on this page"
                                />
                            </th>
                            <th>Calibration ID</th>
                            <th>Date</th>
                            <th>Status</th>
                            <th>Score</th>
                            <th>Uploaded</th>
                            <th>Action</th>
                        </tr>
                    </thead>
                    <tbody>
                        {paginatedRows.map((row) => {
                            const { color, tooltipText } = getStatusInfo(row.status, row.err_msg);
                            const rawProgress = Number(row.progress);
                            const hasValidProgress =
                                Number.isFinite(rawProgress) && rawProgress >= 0 && rawProgress <= 100;
                            const clampedProgress = hasValidProgress
                                ? Math.max(0, Math.min(100, rawProgress))
                                : 0;
                            const reprojectionError = row.reprojection_error ?? row.score;
                            return (
                                <tr key={row.id} className="patient-row">
                                    <td className="table-cell-checkbox" onClick={(e) => e.stopPropagation()}>
                                        <input
                                            type="checkbox"
                                            checked={selectedCalibIds.has(row.id)}
                                            onChange={() => toggleCalibSelection(row.id)}
                                            aria-label={`Select calibration ${row.id}`}
                                        />
                                    </td>
                                    <td>{row.id}</td>
                                    <td>{row.name}</td>
                                    <td>
                                        {row.status === 'success' ? (
                                            <div className="calibration-status-pill calibration-status-pill--success">
                                                <span>Success</span>
                                                {reprojectionError !== null && reprojectionError !== undefined && (
                                                    <span className="calibration-status-pill__meta">
                                                        • {Number(reprojectionError).toFixed(2)} px
                                                    </span>
                                                )}
                                            </div>
                                        ) : row.status === 'failed' ? (
                                            <Tooltip title={tooltipText || 'Processing failed.'}>
                                                <div className="calibration-status-pill calibration-status-pill--failed">
                                                    <span>Failed</span>
                                                    <span className="calibration-status-pill__icon">
                                                        <Icon icon="bi:info-circle-fill" />
                                                    </span>
                                                </div>
                                            </Tooltip>
                                        ) : row.status === 'processing' && hasValidProgress ? (
                                            <div className="calibration-status-progress">
                                                <div className="calibration-status-progress__header">
                                                    <span className="calibration-status-progress__label">
                                                        {getCalibrationStageLabel(clampedProgress)}
                                                    </span>
                                                    <span className="calibration-status-progress__percent">
                                                        • {Math.floor(clampedProgress)}%
                                                    </span>
                                                </div>
                                                <div className="calibration-status-progress__bar">
                                                    <div
                                                        className="calibration-status-progress__bar-fill"
                                                        style={{ width: `${clampedProgress}%` }}
                                                    />
                                                </div>
                                            </div>
                                        ) : ['ready', 'weak', 'uploading'].includes(row.status) ? (
                                            <div className={`calibration-status-pill calibration-status-pill--${row.status}`}>
                                                <span>{row.status.charAt(0).toUpperCase() + row.status.slice(1)}</span>
                                            </div>
                                        ) : (
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                <span
                                                    style={{
                                                        height: '10px',
                                                        width: '10px',
                                                        backgroundColor: color,
                                                        borderRadius: '50%',
                                                        display: 'inline-block',
                                                    }}
                                                />
                                                <span>{row.status}</span>
                                            </div>
                                        )}
                                    </td>
                                    <td>{row.score !== null && row.score !== undefined ? row.score : '-'}</td>
                                    <td>{row.video_count}</td>
                                    <td className="actions">
                                        <div className="actions">
                                            {['success', 'ready', 'failed', 'weak'].includes(row.status) && (
                                                <CommonButton
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        e.stopPropagation();
                                                        selectCalibrationView(row.id);
                                                    }}
                                                >
                                                    <Icon icon="carbon:view" /> View
                                                </CommonButton>
                                            )}
                                            {row.status === 'ready' && (
                                                <CommonButton
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        handleOpenBoardTypeDialog(e, row.id);
                                                    }}
                                                >
                                                    <Icon icon="ion:push-outline" /> Process
                                                </CommonButton>
                                            )}
                                            {row.status === 'failed' && (
                                                <CommonButton
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        handleOpenBoardTypeDialog(e, row.id);
                                                    }}
                                                >
                                                    <Icon icon="gg:redo" /> Retry
                                                </CommonButton>
                                            )}
                                            <CircleButton
                                                className="delete-icon-btn"
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    handleClickOpen(row.id);
                                                }}
                                            >
                                                <Icon icon="uiw:delete" />
                                            </CircleButton>
                                        </div>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
                <div className="table-pagination">
                    <span>Rows per page:</span>
                    <select
                        value={rowsPerPage}
                        onChange={(e) => { setRowsPerPage(Number(e.target.value)); setPage(0); }}
                    >
                        {[10, 25, 50, 100].map((n) => (
                            <option key={n} value={n}>{n}</option>
                        ))}
                    </select>
                    <span className="pagination-range">
                        {totalRows === 0
                            ? '0-0 of 0'
                            : `${page * rowsPerPage + 1}-${Math.min(page * rowsPerPage + rowsPerPage, totalRows)} of ${totalRows}`}
                    </span>
                    <div className="pagination-actions">
                        <button
                            type="button"
                            onClick={() => setPage((p) => Math.max(0, p - 1))}
                            disabled={page === 0}
                            aria-label="Previous page"
                        >
                            <Icon icon="mdi:chevron-left" />
                        </button>
                        <button
                            type="button"
                            onClick={() => setPage((p) => Math.min(Math.ceil(totalRows / rowsPerPage) - 1, p + 1))}
                            disabled={page >= Math.ceil(totalRows / rowsPerPage) - 1 || totalRows === 0}
                            aria-label="Next page"
                        >
                            <Icon icon="mdi:chevron-right" />
                        </button>
                    </div>
                </div>
            </section>
            {openDialog && (
                <ConfirmModal
                    message="Are you sure you want to delete this calibration?"
                    isLoading={isDeleting}
                    acceptHandler={async () => {
                        setIsDeleting(true);
                        try {
                            await deleteCalibration(currentId);
                            setCalibrations((prev) => prev.filter((c) => c.id !== currentId));
                            handleClose();
                        } catch (err) {
                            console.error("Failed to delete calibration", err);
                            handleClose();
                        } finally {
                            setIsDeleting(false);
                        }
                    }}
                    rejectHandler={handleClose}
                />
            )}
            {trashConfirmOpen && (
                <ConfirmModal
                    message={`Move ${selectedCalibIds.size} calibration(s) to trash? They will be permanently deleted after 30 days.`}
                    acceptHandler={handleTrashConfirm}
                    rejectHandler={() => !isTrashing && setTrashConfirmOpen(false)}
                    isLoading={isTrashing}
                    confirmLabel={isTrashing ? undefined : 'Move to trash'}
                />
            )}
            
            <Dialog
                TransitionComponent={Fade}
                transitionDuration={{ enter: 260, exit: 180 }}
                open={openBoardTypeDialog}
                onClose={handleCloseBoardTypeDialog}
                aria-labelledby="board-type-dialog-title"
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle id="board-type-dialog-title">Select Calibration Board Type</DialogTitle>
                <DialogContent>
                    <Alert severity="info" sx={{ mb: 2 }}>
                        Select the calibration board you used to record the calibration videos.
                    </Alert>
                    <FormLabel component="legend" sx={{ mb: 1, fontWeight: 'bold' }}>
                        Which calibration board are you using?
                    </FormLabel>
                    <RadioGroup
                        value={boardType}
                        onChange={(e) => setBoardType(e.target.value)}
                    >
                        <FormControlLabel
                            value="5x7"
                            control={<Radio />}
                            label={
                                <div>
                                    <strong>5×7 Board (Standard)</strong>
                                    <br />
                                    <small style={{ color: '#666' }}>
                                        100mm squares, 78mm markers - Default board used in most setups
                                    </small>
                                </div>
                            }
                            sx={{ mb: 2, alignItems: 'flex-start', '& .MuiRadio-root': { pt: 0.5 } }}
                        />
                        <FormControlLabel
                            value="8x11"
                            control={<Radio />}
                            label={
                                <div>
                                    <strong>8×11 Board (Large)</strong>
                                    <br />
                                    <small style={{ color: '#666' }}>
                                        80mm squares, 60mm markers - Newer larger calibration board
                                    </small>
                                </div>
                            }
                            sx={{ alignItems: 'flex-start', '& .MuiRadio-root': { pt: 0.5 } }}
                        />
                    </RadioGroup>
                </DialogContent>
                <DialogActions>
                    <CommonButton onClick={handleCloseBoardTypeDialog}>
                        Cancel
                    </CommonButton>
                    <Button
                        onClick={handleProcessCalib}
                        variant="contained"
                        color="primary"
                    >
                        Start Processing
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}

export default CalibrationTable;